/**
 * v0.4後半 月次実績一括入力 第2フェーズ I-6 出力機能 (§8) のAPIクライアント。
 * バックエンド: apps/api/src/routes/attendance-exports.ts (実装・レビュー完了済み)。
 * 設計: docs/design/v04-monthly-attendance-bulk-entry-phase2-detail.md §8
 */
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from './api';

export const ATTENDANCE_EXPORT_GRAINS = ['person_period', 'person_day'] as const;
export type AttendanceExportGrain = (typeof ATTENDANCE_EXPORT_GRAINS)[number];

export type AttendanceExportSensitivity = 'normal' | 'family' | 'sales';
export type AttendanceExportValueKind = 'text' | 'integer' | 'minutes' | 'date' | 'boolean';

// §8-1: 1定義あたりの最大列数 (apps/api/src/lib/attendance-export/catalog.ts と同じ値)
export const ATTENDANCE_EXPORT_MAX_COLUMNS = 60;

export type AttendanceExportCatalogItem = {
  item_key: string;
  grain: AttendanceExportGrain;
  label: string;
  sensitivity: AttendanceExportSensitivity;
  value_kind: AttendanceExportValueKind;
  runnable: boolean;
};

export type AttendanceExportCatalogResponse = {
  person_period: AttendanceExportCatalogItem[];
  person_day: AttendanceExportCatalogItem[];
};

export type AttendanceExportTimeFormat = 'hours_minutes' | 'hours_decimal' | 'minutes';

export type AttendanceExportColumn = {
  column_id: string;
  item_key: string;
  header_label: string;
  format_options_json: string | null;
};

export type AttendanceExportAchievementHomeNodeBasis = 'month_start' | 'month_end' | 'fixed_day';

export type AttendanceExportDefinition = {
  definition_id: string;
  label: string;
  grain: AttendanceExportGrain;
  file_format: 'xlsx' | 'csv';
  filters_json: string | null;
  include_blank_days: boolean;
  achievement_month_offset: number;
  achievement_home_node_basis: AttendanceExportAchievementHomeNodeBasis;
  achievement_basis_day: number | null;
  updated_at: string;
  row_version: number;
  columns: AttendanceExportColumn[];
};

export const fetchAttendanceExportDefinitions = () =>
  apiFetch<{ definitions: AttendanceExportDefinition[] }>('/attendance-exports');

export const fetchAttendanceExportCatalog = () =>
  apiFetch<AttendanceExportCatalogResponse>('/attendance-exports/catalog');

export type CreateAttendanceExportDefinitionInput = {
  label: string;
  grain: AttendanceExportGrain;
  file_format: 'xlsx' | 'csv';
};

export const createAttendanceExportDefinition = (input: CreateAttendanceExportDefinitionInput) =>
  apiFetch<{ definition_id: string }>('/attendance-exports', { method: 'POST', body: JSON.stringify(input) });

export type UpdateAttendanceExportDefinitionInput = {
  label: string;
  grain: AttendanceExportGrain;
  file_format: 'xlsx' | 'csv';
  include_blank_days: boolean;
  achievement_month_offset: number;
  achievement_home_node_basis: AttendanceExportAchievementHomeNodeBasis;
  achievement_basis_day: number | null;
  row_version: number;
};

export const updateAttendanceExportDefinition = (id: string, input: UpdateAttendanceExportDefinitionInput) =>
  apiFetch<{ definition_id: string }>(`/attendance-exports/${id}`, { method: 'PUT', body: JSON.stringify(input) });

export const deleteAttendanceExportDefinition = (id: string) =>
  apiFetch<{ deleted: true }>(`/attendance-exports/${id}`, { method: 'DELETE' });

export type AttendanceExportColumnInput = {
  item_key: string;
  header_label: string;
  format_options_json: string | null;
};

export const replaceAttendanceExportColumns = (id: string, columns: AttendanceExportColumnInput[]) =>
  apiFetch<{ definition_id: string; column_count: number }>(`/attendance-exports/${id}/columns`, {
    method: 'PUT', body: JSON.stringify({ columns }),
  });

/** `buildContentDisposition` (apps/api/src/lib/uploads/mime.ts) が組み立てる
 *  `attachment; filename*=UTF-8''<encoded>` からファイル名を取り出す。
 *  test-runner HIGH是正でテスト対象にするため export する。 */
export const parseFilename = (header: string | null, fallback: string): string => {
  if (!header) return fallback;
  const m = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (!m?.[1]) return fallback;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return fallback;
  }
};

export type RunAttendanceExportInput = {
  start_month: string;
  end_month: string;
  node_ids?: string[];
};

/**
 * `POST /:id/run` はレスポンスがxlsx/csvの生バイナリでJSON success wrapperを経由しないため
 * (apps/api/src/routes/attendance-exports.ts 参照)、共通の `apiFetch` は使えない。
 * エラー時 (400/403/413/429等) は通常どおりJSONラップされたエラーのため `ApiClientError` に載せる。
 */
export const runAttendanceExport = async (
  definitionId: string, input: RunAttendanceExportInput, fallbackFilename: string,
): Promise<{ blob: Blob; filename: string }> => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}/attendance-exports/${definitionId}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(input),
  });
  handleUnauthorized(res.status);
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: { code?: string; message?: string; details?: unknown[] } } | null;
    throw new ApiClientError(res.status, body?.error?.code ?? 'UNKNOWN', body?.error?.message ?? '出力の実行に失敗しました', body?.error?.details);
  }
  const blob = await res.blob();
  const filename = parseFilename(res.headers.get('Content-Disposition'), fallbackFilename);
  return { blob, filename };
};
